/**
 * WorkspaceMain — single-column layout for the AI Workspace.
 * Contains dropdowns, preview + generate button, prompt area, progress, review.
 * Close button pinned upper-right.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { StylePreset, AiWorkspaceScope } from '@/hooks/useAiWorkspaceFlow.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import { WorkspaceShotPreview } from './WorkspaceShotPreview.tsx';
import { WorkspaceReview } from './WorkspaceReview.tsx';
import { DigitalSnow } from './DigitalSnow.tsx';
import { GenerationGrid } from './GenerationGrid.tsx';
import { GenerationPreview } from './GenerationPreview.tsx';
import { PromptPopover } from './PromptPopover.tsx';
import { DropdownPicker } from '@/components/DropdownPicker.tsx';
import { SparkleIcon } from '@/components/icons/SparkleIcon.tsx';
import { useAccountStore } from '@/store/accountStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useTutorialStore, selectExampleTourActive } from '@/store/tutorialStore.ts';
import { parseAspectRatio } from '@/utils/aspectRatio.ts';
import { canAfford } from '@/ai/tokenCosts.ts';
import { capabilities } from '@/capabilities.ts';
import { interceptEmbeddedSignIn } from './embeddedAuthGate.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import { formatTimeEstimate } from '@/components/CostBanner.tsx';
import type { AiWorkspaceTask } from '@/store/uiStore.ts';
import type { WorkspaceStep, ReviewItem, GenerationProgress } from '@/hooks/useAiWorkspaceFlow.ts';
import type { TimelineShot } from '@/types/timeline.ts';
import type { CatalogModel, ModelCategory, CostEstimate } from '@/types/ai.ts';
import type { ScreenplayAction } from '@/types/timeline.ts';
import styles from './AiWorkspace.module.css';

const TIER_LABEL_KEYS = ['tierLabelFast', 'tierLabelQuality', 'tierLabelBest'] as const;

const TASK_LABEL_KEYS: Record<AiWorkspaceTask, string> = {
  enhance: 'taskLabelImage',
  video: 'taskLabelVideo',
  continuous: 'taskLabelFirstLastShotVideo',
};

// Exported since 2026-08-21: the board Enhance dialog renders the same rotating phase line, and
// two copies of these lists would drift apart the first time one is edited.
export const ENHANCE_PHASE_KEYS = [
  'enhancePhaseAnalyzing',
  'enhancePhaseColor',
  'enhancePhaseDetails',
  'enhancePhaseJoke',
  'enhancePhaseLightShadow',
  'enhancePhasePolishing',
  'enhancePhaseFinalTouches',
];

/**
 * The rotating phase line in the CREATE tab's verb.
 *
 * Six of the seven ENHANCE_PHASE_KEYS are verb-neutral and read correctly on either surface; the
 * seventh, enhancePhaseColor, is "Enhancing the color palette..." — the one line carrying a verb
 * the Create tab does not use. Editing that key's VALUE would rename the Enhance tab's copy too,
 * so this substitutes its own key for that one entry. Exactly the board's boardCreatePhaseKeys
 * precedent (boardCreateCopy.ts), and a MAP over the shared list rather than a copy of it, so a
 * phase added or reordered above still flows through.
 */
export function createPhaseKeys(keys: readonly string[]): string[] {
  return keys.map(k => (k === 'enhancePhaseColor' ? 'createPhaseColor' : k));
}

export const VIDEO_PHASE_KEYS = [
  'videoPhaseScene',
  'videoPhaseMotion',
  'videoPhaseRendering',
  'videoPhaseJoke',
  'videoPhaseComposing',
  'videoPhaseFrames',
  'videoPhaseAction',
  'videoPhaseTransitions',
  'videoPhaseTogether',
];

interface Props {
  // Step
  step: WorkspaceStep;

  // Task/Model
  task: AiWorkspaceTask;
  onTaskChange: (task: AiWorkspaceTask) => void;
  continuousAvailable: boolean;
  activeModelId: string;
  catalog: CatalogModel[];
  onSelectModel: (category: 'image' | 'video' | 'tts', modelId: string) => void;
  isGenerating: boolean;
  onOpenHistory: () => void;

  // Scope
  scope: AiWorkspaceScope;
  onScopeChange: (scope: AiWorkspaceScope, sceneId?: number) => void;
  overwriteExisting: boolean;
  onToggleOverwrite: (on: boolean) => void;
  showOverwriteOption: boolean;
  skipApproved: boolean;
  onToggleSkipApproved: (on: boolean) => void;

  // Center content
  isEnhance: boolean;
  currentShot: TimelineShot | null;
  scopeShots: TimelineShot[];

  // Prompt
  editablePrompt: string;
  onPromptChange: (p: string) => void;
  stylePreset: StylePreset;
  onStylePresetChange: (preset: StylePreset) => void;
  generatedPrompt: string;
  editableMetadata: string;
  onMetadataChange: (m: string) => void;
  onPromptReset: () => void;

  // Video options
  generateAudio: boolean;
  onToggleAudio: (on: boolean) => void;
  retimeToMatchVideo: boolean;
  onToggleRetime: () => void;
  audioHint: string;
  onAudioHintChange: (hint: string) => void;

  // Cost / counts
  scopeShotCount: number;
  scopeUnitCount: number;
  costEstimate: CostEstimate;
  estTotalSeconds: number;
  hasClamping: boolean;
  durationLimits: { min: number; max: number } | null;

  // Generating
  progress: GenerationProgress | null;
  completedMedia: Map<string, string>;
  progressItems: Array<{ id: string; completed: boolean; label?: string; shot?: TimelineShot; shotId?: number; sourceThumb?: string }>;
  elapsedSeconds: number;

  // Review
  reviewItems: ReviewItem[];
  onToggleItem: (id: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  acceptedCount: number;

  // Revert / Restore
  canRevert: boolean;
  canRestore: boolean;
  restoreLabel: string;
  restoreEntries: GenerationHistoryEntry[];
  onRevert: () => void;
  onRestore: (historyId?: string) => void;

  // Actions
  onGenerate: () => void;
  onCancel: () => void;
  onApply: () => void;
  onDiscard: () => void;
  onClose: () => void;
  generateDisabled: boolean;

  // Preview + queue reordering
  previewShotId: number | null;
  onPreviewShot: (shotId: number | null) => void;
  onRetry: (shotId: number) => void;
  onPrioritize: (shotId: number) => void;
  onTouchUpFromPreview: (shotId: number, prompt: string) => void;

  // Create tab (2026-08-25). This component is shared by every tab and branches on `isEnhance`
  // (the uiStore TASK), never on the active tab, so a Create run identifies itself here.
  /** Which handler started the run in flight. Optional so no existing caller has to pass it.
   *  'import' is not a run at all — a dropped file parked in the same review (2026-08-25). */
  generationOrigin?: 'enhance' | 'touchup' | 'create' | 'import' | null;
  /** The user's typed description — shown in place of the thumbnail while a Create run generates. */
  createPrompt?: string;
  /** Create's "Add as new shot" checkbox — swaps the review footer's Apply label. */
  createAddAsNewShot?: boolean;
  /** True while Apply's Unity round trip is in flight; freezes Discard + Apply. */
  applyBusy?: boolean;

  // Add Actors / Add Scene
  hasScreenplay: boolean;
  sceneActions: ScreenplayAction[];
}

export function WorkspaceMain({
  step,
  task, onTaskChange, continuousAvailable, activeModelId, catalog, onSelectModel, isGenerating, onOpenHistory,
  scope, onScopeChange, overwriteExisting, onToggleOverwrite, showOverwriteOption, skipApproved, onToggleSkipApproved,
  isEnhance, currentShot, scopeShots,
  editablePrompt, onPromptChange,
  stylePreset, onStylePresetChange,
  editableMetadata, onPromptReset,
  generateAudio, onToggleAudio, retimeToMatchVideo, onToggleRetime,
  audioHint, onAudioHintChange,
  scopeShotCount, scopeUnitCount, costEstimate, estTotalSeconds,
  progress, completedMedia, progressItems, elapsedSeconds,
  reviewItems, onToggleItem, onAcceptAll, onRejectAll, acceptedCount,
  canRevert, canRestore, restoreLabel, restoreEntries, onRevert, onRestore,
  onGenerate, onCancel, onApply, onDiscard, onClose, generateDisabled,
  previewShotId, onPreviewShot, onRetry, onPrioritize, onTouchUpFromPreview,
  hasScreenplay, sceneActions, generatedPrompt,
  generationOrigin = null, createPrompt = '', createAddAsNewShot = false, applyBusy = false,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const isCreateRun = generationOrigin === 'create';

  // Project aspect for the generating hero's ratio box — same derivation as GenerationGrid and
  // ShotGrid. The CSS keeps `aspect-ratio: 16 / 9` as the base rule (it is load-bearing for the
  // conic ring's shrink-to-fit geometry); this inline style overrides it when the project is not
  // 16:9, so the dark matte and the ring hug the actual frame instead of a 16:9 box around it.
  const project = useProjectStore(s => s.project);
  const progressAspect = project ? parseAspectRatio(project.aspectRatio) : 0;
  const [restoreMenuOpen, setRestoreMenuOpen] = useState(false);
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(false);
  const toggleTaskDropdown = useCallback(() => setTaskDropdownOpen(v => !v), []);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const toggleModelDropdown = useCallback(() => setModelDropdownOpen(v => !v), []);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const toggleScopeDropdown = useCallback(() => setScopeDropdownOpen(v => !v), []);
  const [promptPopoverOpen, setPromptPopoverOpen] = useState(false);
  const promptPillRef = useRef<HTMLButtonElement>(null);
  const previewSourceRect = useRef<DOMRect | null>(null);
  const cellRefsRef = useRef<Map<string, HTMLDivElement>>(new Map());


  // Sync metadata into image/video prompt: metadata changes → rebuild combined prompt
  const prevMetadataRef = useRef(editableMetadata);
  useEffect(() => {
    if (editableMetadata === prevMetadataRef.current) return;
    prevMetadataRef.current = editableMetadata;
    const combined = editableMetadata
      ? `${editableMetadata}. ${generatedPrompt}`
      : generatedPrompt;
    onPromptChange(combined);
  }, [editableMetadata, generatedPrompt, onPromptChange]);

  // Derive gender summary from scopeShots cast
  const actorSummary = useMemo(() => {
    const seen = new Set<string>();
    let men = 0;
    let women = 0;
    let other = 0;
    for (const shot of scopeShots) {
      if (shot.cast) shot.cast.forEach(c => {
        if (seen.has(c.name)) return;
        seen.add(c.name);
        const g = c.gender?.toLowerCase();
        if (g === 'male') men++;
        else if (g === 'female') women++;
        else other++;
      });
    }
    if (men + women + other === 0) return '';
    const parts: string[] = [];
    if (men > 0) parts.push(`${men} ${men === 1 ? 'man' : 'men'}`);
    if (women > 0) parts.push(`${women} ${women === 1 ? 'woman' : 'women'}`);
    if (other > 0) parts.push(`${other} ${other === 1 ? 'person' : 'people'}`);
    return `${parts.join(', ')} in shot`;
  }, [scopeShots]);

  // Derive scene description text from actions
  const sceneDescriptionText = useMemo(() => {
    if (sceneActions.length === 0) return '';
    return sceneActions
      .sort((a, b) => a.scriptOrder - b.scriptOrder)
      .slice(0, 3)
      .map(a => a.actionText)
      .join(' ');
  }, [sceneActions]);



  const accountUser = useAccountStore(s => s.accountUser);
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);
  const standaloneAuthMode = useAccountStore(s => s.standaloneAuthMode);

  const glowTutorialStep = useUiStore(s => s.glowTutorialStep);
  const setGlowTutorialStep = useUiStore(s => s.setGlowTutorialStep);
  const isTutorialGlow = glowTutorialStep === 'enhance' && isEnhance;
  // Example-tour render beat spends nothing (baked sketch) — the paywall pre-flight must not hijack it.
  // 20260921 — and that is EXAMPLE-ONLY by name as well as by intent now (design §2.1): a
  // characters run's generations are real spends and must meet the pre-flight like any other.
  const tutorialRenderActive = useTutorialStore(s => selectExampleTourActive(s) && s.step === 'render');
  // Tour step 4, last beat: the review screen's Apply Enhancement is the next tap the card
  // asks for, so it glows too. Derived from props already here — no new store state.
  const isTutorialApplyGlow = tutorialRenderActive && isEnhance && step === 'review';

  const insufficient = accountBalance && !accountBalance.isBeta &&
    !canAfford(accountBalance, costEstimate.totalTokens);
  const needsAuth = !accountUser;

  const category: ModelCategory = isEnhance ? 'image' : 'video';
  const models = catalog
    .filter(m => m.category === category && m.id !== 'flux2-flash' && m.id !== 'kling-v3')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const maxSeconds = Math.max(...models.map(m => m.estSeconds), 1);
  const selectedModel = models.find(m => m.id === activeModelId);
  const selectedIdx = models.findIndex(m => m.id === activeModelId);
  const selectedTier = selectedIdx >= 0 && selectedIdx < TIER_LABEL_KEYS.length
    ? t(TIER_LABEL_KEYS[selectedIdx])
    : selectedModel?.name ?? 'Select';

  // For continuous mode, use the pair shots; otherwise use first scope shot
  const fromShot = task === 'continuous' && scopeShots.length >= 1 ? scopeShots[0] : (scopeShots[0] ?? currentShot);
  const toShot = task === 'continuous' && scopeShots.length >= 2 ? scopeShots[1] : null;

  const showDropdowns = step === 'options';

  // Build scope label for dropdown trigger
  const getScopeLabel = () => {
    if (scope === 'selectedScenes') return t('scopeLabelSelectedScenes');
    if (scope === 'selectedShots') return t('scopeLabelSelectedShots');
    return t('scopeLabelAllShots');
  };

  // Generate button label
  const unitCount = isEnhance ? scopeShotCount : scopeUnitCount;
  const buttonLabel = isEnhance
    ? (unitCount > 1 ? t('enhanceShotsPlural', { count: unitCount }) : t('enhanceShotSingular'))
    : (unitCount > 1 ? t('generateVideosPlural', { count: unitCount }) : t('generateVideoSingular'));

  return (
    <div className={styles.main}>
      {/* ── Options step ─────────────────────────────────────── */}
      {showDropdowns && (
        <>
          {/* Controls + Preview row */}
          <div className={styles.controlsRow}>
          {/* Left column: pills + action bar */}
          <div className={styles.controlsLeft}>
          {/* ── Pill row: Scope → Task → Model ── */}
          <div className={styles.pillRow}>
            {task !== 'continuous' && (
              <div className={styles.pillWrapper}>
                <DropdownPicker
                  selectedLabel={getScopeLabel()}
                  open={scopeDropdownOpen}
                  onToggle={toggleScopeDropdown}
                >
                  <div className={styles.pickerList}>
                    {[
                      { value: 'selectedShots', label: t('scopeLabelSelectedShots') },
                      { value: 'selectedScenes', label: t('scopeLabelSelectedScenes') },
                      { value: 'all', label: t('scopeLabelAllShots') },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`${styles.pickerItem} ${opt.value === scope ? styles.pickerItemSelected : ''}`}
                        onClick={() => {
                          onScopeChange(opt.value as AiWorkspaceScope);
                          setScopeDropdownOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </DropdownPicker>
              </div>
            )}

            <div className={styles.pillWrapper}>
              <DropdownPicker
                selectedLabel={t(TASK_LABEL_KEYS[task])}
                open={taskDropdownOpen}
                onToggle={toggleTaskDropdown}
              >
                <div className={styles.pickerList}>
                  {(['enhance', 'video', 'continuous'] as const).map(tk => (
                    <button
                      key={tk}
                      type="button"
                      className={`${styles.pickerItem} ${tk === task ? styles.pickerItemSelected : ''} ${(isGenerating || (tk === 'continuous' && !continuousAvailable)) ? styles.pickerItemDisabled : ''}`}
                      disabled={isGenerating || (tk === 'continuous' && !continuousAvailable)}
                      onClick={() => {
                        onTaskChange(tk);
                        setTaskDropdownOpen(false);
                      }}
                    >
                      {t(TASK_LABEL_KEYS[tk])}
                      {tk === 'continuous' && !continuousAvailable && (
                        <span className={styles.pickerItemHint}>{t('needsNextShot')}</span>
                      )}
                    </button>
                  ))}
                </div>
              </DropdownPicker>
            </div>

            <div className={styles.pillWrapper}>
              <DropdownPicker
                selectedLabel={`${selectedTier} ${selectedModel?.costPerSecond != null ? `${selectedModel.costPerSecond}${t('creditSuffix')}/s` : `${selectedModel?.cost ?? ''}${t('creditSuffix')}`}`}
                open={modelDropdownOpen}
                onToggle={toggleModelDropdown}
              >
                <div className={styles.tierCards}>
                  {models.map((m, i) => {
                    const selected = m.id === activeModelId;
                    const tierLabel = i < TIER_LABEL_KEYS.length ? t(TIER_LABEL_KEYS[i]) : m.name;
                    const speedPct = Math.round(((maxSeconds - m.estSeconds) / maxSeconds) * 100 + 20);

                    return (
                      <button
                        key={m.id}
                        className={`${styles.tierCard} ${selected ? styles.tierCardSelected : ''}`}
                        onClick={() => {
                          onSelectModel(category, m.id);
                          setModelDropdownOpen(false);
                        }}
                        disabled={isGenerating}
                      >
                        <div className={styles.tierHeader}>
                          <span className={styles.tierLabel}>{tierLabel}</span>
                          <span className={styles.tierCost}>{m.costPerSecond != null ? `${m.costPerSecond}${t('creditSuffix')}/s` : `${m.cost} ${t('creditSuffix')}`}</span>
                        </div>
                        <div className={styles.tierSpeed}>
                          <div className={styles.tierSpeedFill} style={{ width: `${Math.min(100, speedPct)}%` }} />
                        </div>
                        <div className={styles.tierMeta}>
                          <span>{m.name}</span>
                          <span>~{m.estSeconds}s</span>
                          {m.resolution && <span className={styles.tierBadge}>{m.resolution}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={() => {
                    setModelDropdownOpen(false);
                    onOpenHistory();
                  }}
                  style={{
                    display: 'block', width: '100%', padding: '8px 10px',
                    background: 'transparent',
                    border: 'none', borderRadius: 4,
                    borderTop: '1px solid var(--chrome-overlay-weak)',
                    color: 'var(--text-secondary)',
                    fontSize: 12, textAlign: 'left', cursor: 'pointer',
                    marginTop: 2,
                    opacity: isGenerating ? 0.35 : 1,
                  }}
                >
                  {isEnhance ? t('useSavedImage') : t('useSavedVideo')}
                </button>
              </DropdownPicker>
            </div>
          </div>

          {/* ── Action bar: checkboxes stacked left, generate right ── */}
          <div className={styles.actionBar}>
            <div className={styles.actionBarLeft}>
              {task !== 'continuous' && (
                <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={skipApproved} onChange={e => onToggleSkipApproved(e.target.checked)} />
                  {t('skipApprovedShots')}
                </label>
              )}
              {task !== 'continuous' && scope === 'all' && showOverwriteOption && (
                <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={overwriteExisting} onChange={e => onToggleOverwrite(e.target.checked)} />
                  {t('overwriteExistingStatic')}
                </label>
              )}
              {task === 'continuous' && toShot && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {t('continuousEndFrameHint', { shot: toShot.number ?? '?' })}
                </div>
              )}
              {(canRevert || canRestore) && (
                <div className={styles.revertRow}>
                  {canRevert && (
                    <button type="button" className={styles.revertPill} onClick={onRevert}>{t('revert')}</button>
                  )}
                  {canRestore && restoreEntries.length <= 1 && (
                    <button type="button" className={styles.revertPill} onClick={() => onRestore()}>{restoreLabel}</button>
                  )}
                  {canRestore && restoreEntries.length > 1 && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button type="button" className={styles.revertPill} onClick={() => setRestoreMenuOpen(o => !o)}>
                        {restoreLabel} ({restoreEntries.length}) ▾
                      </button>
                      {restoreMenuOpen && (<>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setRestoreMenuOpen(false)} />
                        <div className={styles.restoreMenu}>
                          {[...restoreEntries].reverse().map((entry, i) => (
                            <button
                              key={entry.id}
                              type="button"
                              className={styles.restoreMenuItem}
                              onClick={() => { onRestore(entry.id); setRestoreMenuOpen(false); }}
                            >
                              {entry.objectUrl && (
                                <img src={entry.objectUrl} className={styles.restoreMenuThumb} alt="" />
                              )}
                              <span className={styles.restoreMenuLabel}>
                                {entry.assetType === 'video' ? t('useSavedVideo') : t('useSavedImage')} #{restoreEntries.length - i}
                                <br />
                                <span style={{ fontSize: 10, opacity: 0.6 }}>{entry.modelId}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </>)}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* ── Row 2: style presets + prompt pill ── */}
          <div className={styles.bottomRow}>
            {isEnhance ? (
              <>
                {(['photo', 'sketch'] as const).map(p => (
                  <button
                    key={p}
                    className={`${styles.stylePresetBtn} ${stylePreset === p ? styles.stylePresetBtnActive : ''}`}
                    onClick={() => onStylePresetChange(p)}
                  >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
                ))}
              </>
            ) : (
              <>
                <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap', flex: 1 }}>
                  <input type="checkbox" checked={generateAudio} onChange={e => onToggleAudio(e.target.checked)} />
                  {t('generateAiAudio')}
                </label>
                <label className={styles.checkboxRow} style={{ whiteSpace: 'nowrap', flex: 1 }}>
                  <input type="checkbox" checked={retimeToMatchVideo} onChange={onToggleRetime} />
                  {t('retimeShotsToMatchVideo')}
                </label>
              </>
            )}
            <button
              ref={promptPillRef}
              type="button"
              className={styles.editPromptPill}
              onClick={() => setPromptPopoverOpen(v => !v)}
            >
              {t('promptPill', 'Prompt')} &#9998;
            </button>
          </div>

          {/* ── Row 3: generate button centered ── */}
          <div className={styles.generateRow}>
            {needsAuth && (!capabilities.bridge || standaloneAuthMode) ? (
              <button className={styles.authBtn} onClick={() => { onClose(); setAuthDialogOpen(true); }}>
                <SparkleIcon />
                {isEnhance ? t('signInToEnhance') : t('signInToGenerate')}
              </button>
            ) : (
              <button
                className={`${styles.generateBtn} ${isTutorialGlow ? styles.tutorialGlow : ''}`}
                onClick={async () => {
                  if (interceptEmbeddedSignIn(needsAuth)) return;
                  // NOTE: this options branch is currently unreachable (AiWorkspace renders
              // WorkspaceMain only when step !== 'options'; StyleGradeTab owns that screen).
              // Kept in sync with StyleGradeTab's tutorialFreeRender guard anyway: the
              // bypass must stay co-extensive with useAiWorkspaceFlow's baked-render route
              // (single-shot enhance during the render beat), never the whole beat.
              if (insufficient && !(tutorialRenderActive && isEnhance && scopeShots.length === 1)) {
                    const store = useAccountStore.getState();
                    if (!store.welcomeDepletionDismissedThisSession) {
                      const hasPurchased = await store.checkHasEverPurchased();
                      if (!hasPurchased) {
                        onClose();
                        store.setWelcomeDepletionOpen(true);
                        return;
                      }
                    }
                    onClose();
                    trackEvent('depletion_shown', { context: 'ai_panel' });
                    setTokenPurchaseDialogOpen(true);
                    return;
                  }
                  if (isTutorialGlow) setGlowTutorialStep(null);
                  onGenerate();
                }}
                disabled={generateDisabled}
                data-glow-target="enhance"
              >
                <SparkleIcon />
                {buttonLabel}
                <span className={styles.creditBadge}>
                  ~{costEstimate.totalTokens}cr
                  {estTotalSeconds > 0 && ` · ${formatTimeEstimate(estTotalSeconds)}`}
                </span>
              </button>
            )}
          </div>

          </div>{/* end controlsLeft */}

          {/* Thumbnail — fills right half */}
          <div className={styles.previewFloat}>
            <WorkspaceShotPreview
              shot={fromShot}
              toShot={toShot}
              isContinuous={task === 'continuous'}
            />
          </div>
          </div>{/* end controlsRow */}
        </>
      )}

      {/* ── Options step content ─────────────────────────────── */}
      {step === 'options' && (
        <div className={styles.optionsBlock}>
          {/* Video-specific options */}
          {!isEnhance && (
            <div className={styles.videoOptions}>
              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={generateAudio} onChange={e => onToggleAudio(e.target.checked)} />
                {t('generateAiAudio')}
              </label>
              {generateAudio && (
                <input
                  type="text"
                  className={styles.audioHintInput}
                  placeholder={t('audioDirectionPlaceholder')}
                  value={audioHint}
                  onChange={e => onAudioHintChange(e.target.value)}
                />
              )}
              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={retimeToMatchVideo} onChange={onToggleRetime} />
                {t('retimeShotsToMatchVideo')}
              </label>
            </div>
          )}

          {/* Revert/Restore pills moved into actionBar above */}
        </div>
      )}

      {/* ── Generating step ──────────────────────────────────── */}
      {step === 'generating' && (() => {
        const total = progressItems.length;
        const current = completedMedia.size;
        const pct = total > 0 ? Math.round((current / total) * 100) : 0;
        const perUnitSeconds = estTotalSeconds / Math.max(total, 1);
        const remaining = Math.max(0, Math.round((total - current) * perUnitSeconds) - (elapsedSeconds % Math.max(Math.round(perUnitSeconds), 1)));
        const isSingle = total <= 1;
        const timePct = estTotalSeconds > 0
          ? Math.min(0.95, elapsedSeconds / estTotalSeconds)
          : 0;
        const ringProgress = isSingle ? timePct : current / total;

        // Rotating phase messages. A Create run swaps the one Enhance-verbed line; the video list
        // carries no such verb, so it is used unchanged on either surface.
        const basePhaseKeys = isEnhance ? ENHANCE_PHASE_KEYS : VIDEO_PHASE_KEYS;
        const phaseKeys = isEnhance && isCreateRun ? createPhaseKeys(basePhaseKeys) : basePhaseKeys;
        const phaseIdx = Math.floor(elapsedSeconds / 5) % phaseKeys.length;

        // Shot thumbnail for single-shot display — prefer the shot being generated
        const thumbShot = progressItems[0]?.shot ?? scopeShots[0] ?? currentShot;
        const thumbUrl = thumbShot?.thumbnailDataUrl;
        // A Create run has no source image by definition — the shot may never have had one — so the
        // hero must not be gated on a thumbnail or it silently degrades to the batch bar layout
        // (no ring, Cancel below the text). It shows the prompt instead.
        const showHero = isSingle && (!!thumbUrl || isCreateRun);

        return (
          <div className={`${styles.progressSection} ${isSingle ? styles.progressSectionSingle : ''}`}>
            {/* Falling digital snow + wave sweep */}
            <DigitalSnow />

            {/* Batch counter */}
            {!isSingle && (
              <div className={styles.progressCounter}>{current}/{total}</div>
            )}

            {/* Single-shot: horizontal layout — thumbnail left, text right */}
            {showHero ? (
              <div className={styles.progressHero}>
                <div
                  className={styles.progressThumbFrame}
                  style={{ '--ring-progress': ringProgress } as React.CSSProperties}
                >
                  <div
                    className={styles.progressThumbInner}
                    style={progressAspect > 0 ? { aspectRatio: String(progressAspect) } : undefined}
                  >
                    {isCreateRun ? (
                      <div className={styles.promptPreviewBox} data-testid="progress-prompt-box">
                        {/* Inner span: the plate is the flex box that centres it, and
                            -webkit-line-clamp needs its own display:-webkit-box element. */}
                        <span className={styles.promptPreviewText}>{createPrompt}</span>
                      </div>
                    ) : (
                      <img src={thumbUrl} alt="" />
                    )}
                    <div className={styles.shimmerOverlay} />
                  </div>
                </div>
                <div className={styles.progressTextBlock}>
                  <div className={styles.progressStatus}>
                    {progress?.message ?? t('preparing')}
                  </div>
                  {remaining > 0 && (
                    <div className={styles.progressRemaining}>{t('remainingSeconds', { seconds: remaining })}</div>
                  )}
                  <div key={phaseIdx} className={styles.progressPhase}>
                    {t(phaseKeys[phaseIdx])}
                  </div>
                  <div className={styles.progressMeta}>
                    <span>{catalog.find(m => m.id === activeModelId)?.name ?? activeModelId}</span>
                    <span className={styles.progressMetaDot} />
                    <span>{t('creditsUnit', { count: costEstimate.totalTokens })}</span>
                  </div>
                  <button className={styles.cancelBtn} onClick={onCancel}>{t('cancel')}</button>
                </div>
              </div>
            ) : (
              <>
                {/* Fallback: no thumbnail or batch — progress bar + vertical text */}
                {!showHero && (
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{
                      width: isSingle
                        ? `${Math.min(95, Math.round(timePct * 100))}%`
                        : `${pct}%`,
                    }} />
                  </div>
                )}
                <div className={styles.progressTextBlock}>
                  <div className={styles.progressStatus}>
                    {progress?.message ?? t('preparing')}
                  </div>
                  {remaining > 0 && (
                    <div className={styles.progressRemaining}>{t('remainingSeconds', { seconds: remaining })}</div>
                  )}
                  <div key={phaseIdx} className={styles.progressPhase}>
                    {t(phaseKeys[phaseIdx])}
                  </div>
                  <div className={styles.progressMeta}>
                    <span>{catalog.find(m => m.id === activeModelId)?.name ?? activeModelId}</span>
                    <span className={styles.progressMetaDot} />
                    <span>{t('creditsUnit', { count: costEstimate.totalTokens })}</span>
                  </div>
                </div>
                <button className={styles.cancelBtn} onClick={onCancel}>{t('cancel')}</button>
              </>
            )}

            {/* Batch grid */}
            {!isSingle && (
              <GenerationGrid
                items={progressItems}
                completedMedia={completedMedia}
                isEnhance={isEnhance}
                cellRefsRef={cellRefsRef}
                onCellClick={(_itemId, shotId, cellEl) => {
                  previewSourceRect.current = cellEl.getBoundingClientRect();
                  onPreviewShot(shotId);
                }}
              />
            )}

            <AnimatePresence>
              {previewShotId !== null && (
                <GenerationPreview
                  key="generation-preview"
                  previewShotId={previewShotId}
                  progressItems={progressItems}
                  completedMedia={completedMedia}
                  isEnhance={isEnhance}
                  sourceRect={previewSourceRect.current}
                  cellRefsRef={cellRefsRef}
                  catalog={catalog}
                  activeModelId={activeModelId}
                  costEstimate={costEstimate}
                  onPreviewShot={onPreviewShot}
                  onRetry={onRetry}
                  onPrioritize={onPrioritize}
                  onTouchUpFromPreview={onTouchUpFromPreview}
                />
              )}
            </AnimatePresence>
          </div>
        );
      })()}

      {/* ── Review step ──────────────────────────────────────── */}
      {step === 'review' && (() => {
        const isSingle = reviewItems.length <= 1;
        const singleSuccess = isSingle && reviewItems[0]?.success;
        return (
          <>
            <WorkspaceReview
              items={reviewItems}
              isEnhance={isEnhance}
              onToggleItem={onToggleItem}
              onAcceptAll={onAcceptAll}
              onRejectAll={onRejectAll}
              isSingle={isSingle}
            />
            <div className={styles.reviewFooter}>
              {isSingle ? (
                singleSuccess ? (
                  <>
                    <button className={styles.cancelBtn} onClick={onDiscard} disabled={applyBusy}>{t('discard')}</button>
                    {/* .applyBtn composes generateBtn, so .generateBtn.tutorialGlow matches with no new CSS. */}
                    <button
                      className={`${styles.applyBtn} ${isTutorialApplyGlow ? styles.tutorialGlow : ''}`}
                      data-glow-target="apply"
                      onClick={onApply}
                      disabled={applyBusy}
                    >
                      {/* "Add Shot" whenever this Create run is going to create the shot it
                          applies to — image or video arm alike; creating a shot is a different
                          action and gets a different word. Otherwise image = "Apply"
                          (applyGenerated), video keeps applyVideo verbatim.
                          An IMPORT review is always an image, so it always says applyGenerated —
                          stated explicitly rather than leaning on isEnhance, which is true only
                          because startImportReview mirrors the shared task on the way in. */}
                      {isCreateRun && createAddAsNewShot
                        ? t('applyAddShot')
                        : ((isEnhance || generationOrigin === 'import') ? t('applyGenerated') : t('applyVideo'))}
                    </button>
                  </>
                ) : (
                  <button className={styles.cancelBtn} onClick={onClose}>{t('close')}</button>
                )
              ) : (
                <>
                  <span
                    className={styles.reviewCount}
                    dangerouslySetInnerHTML={{ __html: isEnhance
                      ? t('enhancementsSelected', { count: acceptedCount, plural: acceptedCount !== 1 ? 's' : '' })
                      : t('videosSelected', { count: acceptedCount, plural: acceptedCount !== 1 ? 's' : '' })
                    }}
                  />
                  {acceptedCount > 0 ? (
                    <>
                      <button className={styles.cancelBtn} onClick={onDiscard}>{t('discard')}</button>
                      <button className={styles.applyBtn} onClick={onApply}>
                        {isEnhance
                          ? t('applyCountEnhancements', { count: acceptedCount, plural: acceptedCount !== 1 ? 's' : '' })
                          : t('applyCountVideos', { count: acceptedCount, plural: acceptedCount !== 1 ? 's' : '' })}
                      </button>
                    </>
                  ) : (
                    <button className={styles.cancelBtn} onClick={onClose}>{t('close')}</button>
                  )}
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* ── Prompt Popover ──────────────────────────────────── */}
      <PromptPopover
        open={promptPopoverOpen}
        onClose={() => setPromptPopoverOpen(false)}
        anchorEl={promptPillRef.current}
        prompt={editablePrompt}
        onPromptChange={onPromptChange}
        onReset={onPromptReset}
        actorSummary={actorSummary}
        sceneDescription={sceneDescriptionText}
        hasScreenplay={hasScreenplay}
        metadataPreview={editableMetadata}
        showMetadataPreview={scope === 'all'}
        isEnhance={isEnhance}
      />
    </div>
  );
}
